import * as React from 'react';
import { connect } from 'react-redux';
import { goBack } from 'react-router-redux';
import {
  getCompanyBookings,
  getCompany,
  getCompanyPayments,
  getRevision,
} from '../../redux/actions/companies';

import {
  IconButton,
  BookingRelationList,
  CompanyAvatar,
  CompanyUpdateForm,
  HeadingSection,
  NoteList,
  Permission,
  RouteWrapper,
  PaymentTable,
  Section,
  Tooltip,
} from '../../components';

interface Props {
  companies: any;
  match: any;
  vehicleTypes: Array<any>;
  dispatch: Function;
}

interface State {
  id: number;
  revisionId?: number;
  company: any;
  ready: boolean;
  section: 0 | 1 | 2;
  bookingsReady: boolean;
  bookings: Array<any>;
  error?: string;
}

class Route extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      id: Number(this.props.match.params.id),
      revisionId: Number(this.props.match.params.revisionId),
      company: false,
      ready: false,
      section: 0,
      bookingsReady: true,
      bookings: [],
    };
    this.getCompanyBookings = this.getCompanyBookings.bind(this);
  }

  /**
   * Load company from the API
   */
  async componentDidMount() {
    let response;
    if (this.state.revisionId) {
      response = await this.props.dispatch(
        getRevision(this.state.id, this.state.revisionId),
      );
    } else {
      response = await this.props.dispatch(getCompany(this.state.id));
    }
    if (response.error) {
      this.setState({ error: response.error });
      return;
    }
    this.setState({
      company: response.data,
      ready: true,
    });
  }

  /**
   * Get the bookings assigned to the company
   */
  async getCompanyBookings(page: number) {
    this.setState({ bookingsReady: false });
    const bookings = await this.props.dispatch(
      getCompanyBookings(this.state.id, page),
    );
    this.setState({ bookings: bookings.data, bookingsReady: true });
  }

  render() {
    return (
      <RouteWrapper
        requireAuth
        requirePermission="MANAGE_COMPANIES"
        title="Company"
        breadcrumbs={[
          { link: '/companies', text: 'Companies' },
          {
            link: `/companies/${this.state.id}`,
            text: this.state.company.name || 'Company',
          },
        ]}
        ready={this.state.ready}
        error={this.state.error}
      >
        <HeadingSection
          title={this.state.company.name}
          image={<CompanyAvatar company={this.state.company} />}
          controls={
            !!this.state.revisionId
              ? []
              : [
                  <Permission require="MANAGE_DELETIONS" key="delete">
                    {this.state.bookings.length > 0 ? (
                      <Tooltip text="Company is attached to a booking">
                        <button className="button is-static">
                          Deletion disabled
                        </button>
                      </Tooltip>
                    ) : (
                      <IconButton
                        link={`/companies/${this.state.id}/delete`}
                        className="is-danger is-outlined"
                        icon="trash"
                      >
                        Delete
                      </IconButton>
                    )}
                  </Permission>,
                  <IconButton
                    key="back-button"
                    icon="undo"
                    className="is-default"
                    handleClick={() => {
                      this.props.dispatch(goBack());
                    }}
                  >
                    Return
                  </IconButton>,
                ]
          }
        />
        <Section>
          <div className="tabs is-boxed">
            <ul>
              {['Details', 'Bookings', 'Payments'].map((name, index) => (
                <li
                  key={name}
                  className={this.state.section === index ? 'is-active' : ''}
                >
                  <a
                    onClick={() => {
                      this.setState({ section: index as 0 | 1 });
                    }}
                  >
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="columns">
            <div className="column is-12-desktop is-7-widescreen">
              {this.state.section === 0 && (
                <CompanyUpdateForm
                  company={this.state.company}
                  isDisabled={!!this.state.revisionId}
                  handleUpdate={() => {
                    window.history.back();
                  }}
                />
              )}
              {this.state.section === 1 && (
                <BookingRelationList
                  vehicleTypes={this.props.vehicleTypes}
                  bookings={this.state.bookings}
                  handleUpdate={(page) => this.getCompanyBookings(page)}
                  loading={!this.state.bookingsReady}
                />
              )}
              {this.state.section === 2 && (
                <PaymentTable
                  id={this.state.id}
                  action={getCompanyPayments}
                  vehicleTypes={this.props.vehicleTypes}
                  hideNotes
                />
              )}
            </div>
            <div className="column is-12-desktop is-5-widescreen">
              <NoteList resource="companies" id={this.state.id} />
            </div>
          </div>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ companies }) => ({
  companies,
});

export default connect(mapStateToProps)(Route);
